import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Alert } from 'src/app/interfaces/alert';
import { MembershipSelectStepStates, User } from 'src/app/interfaces/user';
import { LogService } from 'src/app/services/common/log.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { RegistrationProgressData } from 'src/app/services/registration-progress/registration-progress.service';
import { UserService } from 'src/app/services/user/user.service';
import { PricingTableSubTypes } from 'src/app/shared/components/pricing-table/pricing-table.component';
import { environment } from 'src/environments/environment';
import { PlanFeatureService } from '../../../services/common/plan-feature.service';
import { Pet, PetSpeciesFilterableType } from '../../../interfaces/pet';

@Component({
  selector: 'app-membership-options',
  templateUrl: './membership-options.component.html',
  styleUrls: ['./membership-options.component.scss'],
})
export class MembershipOptionsComponent implements OnInit {
  public alert: Alert = null;
  public loading = false;
  public pricingPlans = environment.stripePlans;
  public user: User;
  public showPromo: boolean = false;
  public promoClosed = false;
  public currentMembershipName = 'identibase';
  public userAlreadyHasASubscription = false;
  public mainPlanContent = [];
  public petType: PetSpeciesFilterableType;
  public petName: string;
  public isBaseFlow: boolean;
  public thereIsOnlyOneCat: boolean = false;
  public isOutdoors: boolean = null;
  public appPriceCardFeatureText: string[] = [
    'Unlimited changes to your pet’s information',
    'Download keepership certificate',
    'Online account access and management',
    'Secure data storage'
  ]

  getMainPlanCardContent(petName: string) {
    return this.planFeatureService.getMainPlanContent(petName, this.thereIsOnlyOneCat, this.isOutdoors);
  }

  findPetByType = (type: PetSpeciesFilterableType, pets: Pet[]) => {
    const currentPet = pets.find((pet) => pet.speciesFilter === type);
    return currentPet ? { name: currentPet.name, type } : null;
  };

  categorizePets = (pets: Pet[]) => {
    return pets.reduce(
      (acc, pet) => {
        if (pet.speciesFilter === PetSpeciesFilterableType.Cat) {
          acc.cats.push(pet);
        } else if (pet.speciesFilter === PetSpeciesFilterableType.Dog) {
          acc.dogs.push(pet);
        } else {
          acc.others.push(pet);
        }
        return acc;
      },
      {
        cats: [] as Pet[],
        dogs: [] as Pet[],
        others: [] as Pet[],
      }
    );
  };

  concatNames = (names: Pet[]) => {
    return names.reduce((prev, current, index, array) => {
      if (index === 0) {
        return current.name;
      } else if (index === array.length - 1) {
        return prev + ' and ' + current.name;
      } else {
        return prev + ', ' + current.name;
      }
    }, '');
  };

  constructor(
    private _logService: LogService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _userService: UserService,
    public membershipOptionsService: MemberShipOptionsService,
    public planFeatureService: PlanFeatureService
  ) {
    this.user = this._activatedRoute.snapshot.data.user as User;

    this.userAlreadyHasASubscription = this.membershipOptionsService.doesUserHaveMembership(
      this.user
    );

    if (this.userAlreadyHasASubscription) {
      this.currentMembershipName = this.membershipOptionsService.getUserMembershipName(this.user);
    }

    if (this.user) {
      const petsCategories = this.categorizePets(this.user.pets);

      if (
        (petsCategories.cats.length > 0 && petsCategories.dogs.length > 0) ||
        (petsCategories.cats.length > 0 && petsCategories.others.length > 0) ||
        (petsCategories.others.length > 0 && petsCategories.dogs.length > 0) ||
        petsCategories.others.length > 0
      ) {
        this.isBaseFlow = true;
      } else {
        if (petsCategories.cats.length > 0) {
          this.isBaseFlow = false;
          this.petType = petsCategories.cats[0].speciesFilter;
          this.petName = this.concatNames(petsCategories.cats);
        } else if (petsCategories.dogs.length > 0) {
          this.isBaseFlow = false;
          this.petType = petsCategories.dogs[0].speciesFilter;
          this.petName = this.concatNames(petsCategories.dogs);
        }
      }
    }
  }

  ngOnInit(): void {
    this.isOutdoors = this.user.pets[0].isOutdoors;
    this.thereIsOnlyOneCat = this.user.pets.length === 1 && this.user.pets[0].speciesFilter === PetSpeciesFilterableType.Cat
    this.mainPlanContent = this.getMainPlanCardContent(this.petName);
    if (this.thereIsOnlyOneCat) {
      this.appPriceCardFeatureText = [
        'Unlimited changes to your pet’s information',
        'Download keepership certificate',
        'Online account access and management',
        'Secure data storage',
        'App access'
      ]
    }
  }

  public getPromoVisible(): boolean {
    const currentDate = new Date();
    const promoEndDate = new Date(2023, 11, 1);
    return currentDate < promoEndDate;
  }

  public onClosePromo() {
    this.promoClosed = true;
  }

  public getProgressData() {
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: true,
      isPetChipNumberRegistrationCompleted: true,
      isMembershipSelectionCompleted: false,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }

  public select(planId?: string) {
    switch (planId) {
      case environment.stripePlans.identibasePlus:
        this._router.navigate(['/identibase-plus']);
        break;
      case environment.stripePlans.identiAlertMonthly:
        this._router.navigate(['/identialert']);
        break;
      case environment.stripePlans.identiAlertAnnual:
        this._router.navigate(['/identialert']);
        break;
      case environment.stripePlans.identiAlertFindCat:
        this._router.navigate(['/identifind']);
        break;
      case environment.stripePlans.identiAlertFind:
        this._router.navigate(['/identifind']);
        break;
      default:
        this._router.navigate(['/identialert']);
    }
  }

  public navigateToProductPage(pricingTableSubType: string): void {
    switch (pricingTableSubType) {
      case PricingTableSubTypes.IDENTIBASEPLUS:
        this._router.navigate(['/identibase-plus']);
        break;
      case PricingTableSubTypes.IDENTIALERT:
        this._router.navigate(['/identialert']);
        break;
      case PricingTableSubTypes.IDENTIFIND:
        this._router.navigate(['/identifind']);
        break;
    }
  }

  public continueWithCurrentMembership() {
    this._router.navigate(['/welcome-gift']);
  }

  public completeMembershipSelection() {
    const isPromotionPeriod = this.getPromoVisible();
    if (!this.showPromo && isPromotionPeriod) {
      this.showPromo = true;
      return;
    }

    if (!this.loading) {
      this.updateUserDetails()
        .subscribe(
          (updatedUser) => {
            this.user = updatedUser;
            this.alert = {
              type: 'success',
              message:
                "Your account information was updated. You'll get redirected in a few seconds!",
            };
            setTimeout(() => {
              this._router.navigate(['/welcome-gift']);
            }, 1000);
            return;
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
            this._logService.error(
              'An error has occurred during membership selection save. Please try again later. ',
              err
            );
          }
        )
        .add(() => {
          this.loading = false;
        });
    }
  }

  public closeAlert() {
    this.alert = null;
  }

  private updateUserDetails(): Observable<User> {
    this.closeAlert();
    this.loading = true;
    const userChanges: Partial<User> = {
      membershipSelectStepCompleted:
        this.user.membershipSelectStepCompleted == MembershipSelectStepStates.NotUpdated
          ? MembershipSelectStepStates.Updated
          : this.user.membershipSelectStepCompleted,
    };

    return this._userService.patchMe(userChanges);
  }

  protected readonly PetSpeciesFilterableType = PetSpeciesFilterableType;
}
